import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Form, Input, Row, Select, Space, Table, Tabs, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HeroKpiCard,
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
  SignalBadge,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { eoipService, type EoipIntelligenceCard } from '@/services/eoip.service';
import { eoafService } from '@/services/eoaf.service';
import { EoipMorningBriefPanel } from './EoipMorningBriefPanel';

function priorityTier(p: string): 'critical' | 'watch' | 'healthy' {
  if (p === 'critical' || p === 'high') return 'critical';
  if (p === 'medium') return 'watch';
  return 'healthy';
}

const OperationalIntelligencePage: React.FC = () => {
  const qc = useQueryClient();
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [briefRole, setBriefRole] = useState('CEO');

  const dashQ = useQuery({
    queryKey: ['eoip-dashboard'],
    queryFn: () => eoipService.getDashboard(),
    staleTime: 60_000,
  });
  const briefQ = useQuery({
    queryKey: ['eoip-brief', briefRole],
    queryFn: () => eoipService.getExecutiveBrief(briefRole),
    staleTime: 120_000,
  });
  const watchQ = useQuery({
    queryKey: ['eoip-watchlists'],
    queryFn: () => eoipService.getWatchlists(),
    staleTime: 60_000,
  });

  const feedbackMut = useMutation({
    mutationFn: async (payload: {
      recommendationId: string;
      feedback: 'accepted' | 'rejected' | 'ignored';
    }) => {
      if (payload.feedback === 'accepted') {
        return eoafService.convertFromEoip(payload.recommendationId, 'accepted');
      }
      return eoafService.convertFromEoip(payload.recommendationId, payload.feedback);
    },
    onSuccess: (_data, vars) => {
      message.success(
        vars.feedback === 'accepted'
          ? 'Accepted — operational action created'
          : 'Feedback recorded'
      );
      void qc.invalidateQueries({ queryKey: ['eoip-dashboard'] });
      void qc.invalidateQueries({ queryKey: ['eoaf-dashboard'] });
      void qc.invalidateQueries({ queryKey: ['eoaf-actions'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const watchMut = useMutation({
    mutationFn: eoipService.createWatchlist,
    onSuccess: () => {
      message.success('Watchlist saved');
      void qc.invalidateQueries({ queryKey: ['eoip-watchlists'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const scenarioMut = useMutation({
    mutationFn: eoipService.runScenario,
    onSuccess: () => message.success('Scenario projected (advisory only)'),
    onError: (e: Error) => message.error(e.message),
  });

  const cards = useMemo(() => {
    const all = dashQ.data?.cards ?? [];
    if (priorityFilter === 'all') return all;
    return all.filter((c) => c.priority === priorityFilter);
  }, [dashQ.data?.cards, priorityFilter]);

  const pulse = dashQ.data?.pulse;
  const dims = (pulse?.dimensions || {}) as Record<string, number>;

  return (
    <OiPageFrame
      subtitle="Continuously observe, predict, and recommend — never execute. Advisory operational intelligence."
      loading={dashQ.isLoading && !dashQ.data}
      error={Boolean(dashQ.error)}
      errorMessage="Operational intelligence unavailable"
    >
      {dashQ.data?.degrade?.jobs_disabled ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          EOIP background jobs are disabled (EOIP_JOBS_DISABLED=1). Showing last stored advisory state.
        </Card>
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <HeroKpiCard
            label="Enterprise Pulse"
            value={pulse?.score != null ? String(pulse.score) : '—'}
            score={pulse?.score ?? undefined}
          />
          <div style={{ marginTop: 8, color: 'var(--oi-text-muted)', fontSize: 12 }}>
            {pulse?.statusLabel || 'No pulse yet'} · refreshed {dashQ.data?.refreshed_at?.slice(0, 19) || '—'}
          </div>
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Critical cards" value={cards.filter((c) => c.priority === 'critical').length} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Predictions" value={(dashQ.data?.predictions || []).length} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Opportunities" value={(dashQ.data?.opportunities || []).length} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Recommendations" value={(dashQ.data?.recommendations || []).length} />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {Object.entries(dims).map(([k, v]) => (
          <Col xs={12} sm={8} md={3} key={k}>
            <PrimaryKpiCard label={k} value={v} />
          </Col>
        ))}
      </Row>

      <Tabs
        items={[
          {
            key: 'feed',
            label: 'Intelligence feed',
            children: (
              <Card
                bordered={false}
                title="Prioritized intelligence cards"
                extra={
                  <Select
                    value={priorityFilter}
                    onChange={setPriorityFilter}
                    style={{ width: 160 }}
                    options={[
                      { value: 'all', label: 'All priorities' },
                      { value: 'critical', label: 'Critical' },
                      { value: 'high', label: 'High' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'low', label: 'Low' },
                    ]}
                  />
                }
              >
                {!cards.length ? (
                  <OiEmptyState
                    title="No intelligence cards yet"
                    body="Pipeline will populate after the next EOIP signal run."
                  />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={cards}
                    pagination={{ pageSize: 12 }}
                    columns={[
                      {
                        title: 'Priority',
                        dataIndex: 'priority',
                        width: 110,
                        render: (p: string) => <SignalBadge tier={priorityTier(p)} />,
                      },
                      { title: 'Kind', dataIndex: 'kind', width: 120 },
                      { title: 'Domain', dataIndex: 'domain', width: 120 },
                      { title: 'Headline', dataIndex: 'headline' },
                      {
                        title: 'Why',
                        dataIndex: 'why',
                        ellipsis: true,
                        render: (v: string) => v?.slice(0, 120),
                      },
                      {
                        title: 'Conf.',
                        dataIndex: 'confidence',
                        width: 70,
                      },
                      {
                        title: 'Feedback',
                        width: 220,
                        render: (_: unknown, row: EoipIntelligenceCard) =>
                          row.kind === 'recommendation' ? (
                            <Space size={4}>
                              <Button
                                size="small"
                                onClick={() =>
                                  feedbackMut.mutate({
                                    recommendationId: row.id,
                                    feedback: 'accepted',
                                  })
                                }
                              >
                                Accept
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  feedbackMut.mutate({
                                    recommendationId: row.id,
                                    feedback: 'rejected',
                                  })
                                }
                              >
                                Reject
                              </Button>
                              <Button
                                size="small"
                                type="text"
                                onClick={() =>
                                  feedbackMut.mutate({
                                    recommendationId: row.id,
                                    feedback: 'ignored',
                                  })
                                }
                              >
                                Ignore
                              </Button>
                            </Space>
                          ) : (
                            <Tag>—</Tag>
                          ),
                      },
                    ]}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'brief',
            label: 'Morning brief',
            children: (
              <Card
                bordered={false}
                title="Executive brief"
                extra={
                  <Select
                    value={briefRole}
                    onChange={setBriefRole}
                    style={{ width: 200 }}
                    options={[
                      'CEO',
                      'CFO',
                      'COO',
                      'FACTORY_MANAGER',
                      'PROCUREMENT_MANAGER',
                      'QUALITY_MANAGER',
                      'FINANCE_MANAGER',
                      'PROJECT_DIRECTOR',
                    ].map((r) => ({ value: r, label: r }))}
                  />
                }
              >
                {!briefQ.data ? (
                  <OiEmptyState title="No brief for this role/date" body="Briefs generate with the morning EOIP cron." />
                ) : (
                  <div>
                    <div style={{ marginBottom: 12, color: 'var(--oi-text-muted)' }}>
                      {String(briefQ.data.brief_date || briefQ.data.briefDate || '')} ·{' '}
                      {String(briefQ.data.role_code || briefQ.data.roleCode || briefRole)}
                    </div>
                    <EoipMorningBriefPanel brief={briefQ.data as Record<string, unknown>} />
                  </div>
                )}
              </Card>
            ),
          },
          {
            key: 'watch',
            label: 'Watchlists',
            children: (
              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Card title="Your watchlists" bordered={false}>
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={(watchQ.data as Array<Record<string, unknown>>) || []}
                      columns={[
                        { title: 'Type', dataIndex: 'watch_type' },
                        { title: 'Target', dataIndex: 'target_label' },
                        { title: 'Id', dataIndex: 'target_id', ellipsis: true },
                      ]}
                      locale={{ emptyText: 'No watchlists yet' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={10}>
                  <Card title="Add watchlist" bordered={false}>
                    <Form
                      layout="vertical"
                      onFinish={(v) =>
                        watchMut.mutate({
                          watchType: v.watchType,
                          targetLabel: v.targetLabel,
                          targetId: v.targetId || undefined,
                        })
                      }
                    >
                      <Form.Item name="watchType" label="Watch type" rules={[{ required: true }]}>
                        <Select
                          options={[
                            'supplier',
                            'customer',
                            'project',
                            'machine',
                            'contract',
                            'cash',
                            'inventory',
                            'margin',
                            'domain',
                          ].map((x) => ({ value: x, label: x }))}
                        />
                      </Form.Item>
                      <Form.Item name="targetLabel" label="Label">
                        <Input placeholder="Optional label" />
                      </Form.Item>
                      <Form.Item name="targetId" label="Target id">
                        <Input placeholder="Optional entity id" />
                      </Form.Item>
                      <Button type="primary" htmlType="submit" loading={watchMut.isPending}>
                        Save
                      </Button>
                    </Form>
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'scenario',
            label: 'Scenarios',
            children: (
              <Card title="Deterministic what-if (bounded models)" bordered={false}>
                <Space wrap>
                  <Button
                    onClick={() =>
                      scenarioMut.mutate({ scenarioType: 'supplier_delay', params: { days: 7 } })
                    }
                  >
                    Supplier delay 7d
                  </Button>
                  <Button
                    onClick={() =>
                      scenarioMut.mutate({ scenarioType: 'machine_down', params: { days: 3 } })
                    }
                  >
                    Machine down 3d
                  </Button>
                  <Button
                    onClick={() =>
                      scenarioMut.mutate({
                        scenarioType: 'early_customer_payment',
                        params: { amount: 250000 },
                      })
                    }
                  >
                    Early payment 250k
                  </Button>
                </Space>
                {scenarioMut.data ? (
                  <pre style={{ marginTop: 16, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(scenarioMut.data, null, 2)}
                  </pre>
                ) : null}
              </Card>
            ),
          },
        ]}
      />
    </OiPageFrame>
  );
};

export default OperationalIntelligencePage;
